import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { Download } from "lucide-react";
import { useProject } from "@/context/ProjectContext";
import { mergeSiteSeries } from "@/lib/siteSeries";
import { SiteSeriesLineChart } from "@/components/SiteSeriesLineChart";

/** Portfolio demand or consumption by site over the selected range. */
export function EnergyTrendChart({ kind, days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const { project } = useProject();

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api
      .get("/energy/trends", { params: { kind, days, ...(project ? { project } : {}) } })
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [kind, days, project]);

  const { chartData, siteKeys } = useMemo(() => mergeSiteSeries(data?.sites), [data]);
  const unit = data?.sites?.find((s) => s.unit)?.unit;

  const exportCsv = () =>
    downloadCsv(
      `energy_${kind}_${days}d.csv`,
      ["Timestamp", ...siteKeys.map((k) => (unit ? `${k} (${unit})` : k))],
      chartData.map((r) => [new Date(r.ts).toISOString(), ...siteKeys.map((k) => r[k] ?? "")])
    );

  return (
    <div className="k-card p-6" data-testid={`energy-trend-${kind}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="k-label">{kind === "demand" ? "Electric Demand" : "Energy Consumption"}</div>
          <div className="text-lg font-heading font-semibold text-[#212121]">
            {kind === "demand" ? "kW by site" : "kWh by site"} — last {days}d
          </div>
        </div>
        <button
          onClick={exportCsv}
          disabled={loading || chartData.length === 0}
          data-testid={`energy-export-${kind}-btn`}
          className="k-btn-outline"
        >
          <Download className="h-3 w-3" /> CSV
        </button>
      </div>
      <div className="mt-5 h-80">
        {loading ? (
          <div className="h-full flex items-center justify-center k-sublabel">Loading…</div>
        ) : err ? (
          <div className="text-red-600 text-sm">{err}</div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-[#6B7280]">No meter history available.</div>
        ) : (
          <SiteSeriesLineChart chartData={chartData} siteKeys={siteKeys} unit={unit} />
        )}
      </div>
    </div>
  );
}
